/**
 * Manages the "Made in USA" image overlay for Rio Gallery (Variant Image Wizard)
 * Overlay is positioned on the first/main image and shows/hides based on variant metafield and active slide
 */
(function() {
  'use strict';

  let overlay = null;
  let productMedia = null;
  let marketHandle = null;
  let variantPicker = null;
  let madeInUsaVariantIds = [];
  let slideObserver = null;
  let rioGallery = null;

  function init() {
    productMedia = document.querySelector('#product-media');
    if (!productMedia) {
      setTimeout(init, 200);
      return;
    }

    // Theme previews can emit /cdn/... paths that the local server cannot serve.
    // Resolve Shopify file paths directly against the CDN instead of localhost.
    const imageUrl = (productMedia.dataset.madeInUsaImage || '')
      .replace(/^\/cdn\/s\/files\//, 'https://cdn.shopify.com/s/files/');
    marketHandle = productMedia.dataset.marketHandle;
    const variantIdsStr = productMedia.dataset.madeInUsaVariantIds;
    
    if (variantIdsStr) {
      madeInUsaVariantIds = variantIdsStr.split(',').map(id => id.toString());
    }
    
    if (!imageUrl) return; // No image configured

    // Wait for Rio gallery to be available
    function waitForGallery() {
      rioGallery = productMedia.querySelector('.rio-media-gallery');
      if (!rioGallery) {
        setTimeout(waitForGallery, 200);
        return;
      }

      // Create overlay
      overlay = document.querySelector('.made-in-usa-image-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'made-in-usa-image-overlay';
        overlay.style.cssText = 'pointer-events: none; display: none; position: absolute;';
        
        const img = document.createElement('img');
        img.alt = theme.strings.madeInUsa;
        img.className = 'made-in-usa-image-overlay__img';
        overlay.appendChild(img);
      }

      // Also update reused theme markup; a stale srcset would override the fixed src.
      const overlayImage = overlay.querySelector('img');
      if (overlayImage) {
        overlayImage.removeAttribute('srcset');
        overlayImage.src = imageUrl;
      }

      // Position overlay in the active slide
      positionOverlay();

      setupVariantPicker();
      setupSlideObserver();
    }

    waitForGallery();
  }

  /**
   * Positions the overlay in the active slide container
   */
  function positionOverlay() {
    if (!productMedia || !overlay) return;

    // Rio can replace the gallery or its slides after the variant event fires.
    rioGallery = productMedia.querySelector('.rio-media-gallery');
    if (!rioGallery) return;

    // Find the active slide
    const activeSlide = rioGallery.querySelector('.pwzrswiper-slide-active');
    if (!activeSlide) return;

    // Find the image container within the slide (look for img parent)
    const slideImage = activeSlide.querySelector('img:not(.made-in-usa-image-overlay__img)');
    if (!slideImage) return;

    // Find a suitable container (img's parent that has positioning context)
    let container = slideImage.parentElement;
    while (container && container !== activeSlide) {
      const style = window.getComputedStyle(container);
      if (style.position === 'relative' || style.position === 'absolute' || container === activeSlide) {
        break;
      }
      container = container.parentElement;
    }

    // Use activeSlide if no positioned container found, or ensure activeSlide is positioned
    if (!container || container === activeSlide) {
      container = activeSlide;
      const style = window.getComputedStyle(container);
      if (style.position === 'static') {
        container.style.position = 'relative';
      }
    }

    // Check the actual parent: Rio may remove the badge while reusing the slide.
    if (overlay.parentElement !== container) {
      container.appendChild(overlay);
    }
  }

  /**
   * Checks if the first main slide is currently active
   */
  function isFirstSlideActive() {
    if (!rioGallery) return true;

    const mainSlides = rioGallery.querySelectorAll('.pwzrswiper-slide');
    if (mainSlides.length === 0) return true;

    return mainSlides[0].classList.contains('pwzrswiper-slide-active');
  }

  function updateOverlay(variant) {
    if (!overlay) return;

    // Reposition overlay if slide changed
    positionOverlay();

    // Check market handle first (only show on US market)
    if (marketHandle !== 'us') {
      overlay.style.display = 'none';
      return;
    }

    // Get variant if not provided
    if (!variant && variantPicker) {
      variant = variantPicker.variant;
    }

    if (!variant || !variant.id) {
      overlay.style.display = 'none';
      return;
    }

    // Check if variant ID is in the made in USA list
    const variantIdStr = variant.id.toString();
    const hasMadeInUsa = madeInUsaVariantIds.includes(variantIdStr);

    // Check if first slide is active
    const isFirst = isFirstSlideActive();

    // Show overlay only if variant has madeinusa metafield AND first slide is active
    overlay.style.display = (hasMadeInUsa && isFirst) ? 'block' : 'none';
  }

  /**
   * Restores the badge after Rio rebuilds slides or removes injected nodes.
   */
  function setupSlideObserver() {
    if (!productMedia) return;
    if (slideObserver) slideObserver.disconnect();

    let updateScheduled = false;
    slideObserver = new MutationObserver(() => {
      if (updateScheduled) return;
      updateScheduled = true;
      requestAnimationFrame(() => {
        updateScheduled = false;
        updateOverlay();
      });
    });

    // Observe the stable media wrapper, not slide nodes that Rio replaces.
    slideObserver.observe(productMedia, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'src', 'data-index']
    });
  }

  function setupVariantPicker() {
    variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) {
      setTimeout(setupVariantPicker, 200);
      return;
    }

    // Wait for variant picker data to be ready
    function waitForVariantData() {
      if (variantPicker.data && variantPicker.data.formatted) {
        // Data is ready, set up listener
        variantPicker.addEventListener('on:variant:change', (evt) => {
          updateOverlay(evt.detail?.variant);
        });

        // Initial update after a short delay
        setTimeout(() => updateOverlay(), 500);
      } else {
        // Data not ready yet, wait a bit more (max 10 seconds)
        const attempts = waitForVariantData.attempts || 0;
        if (attempts < 50) {
          waitForVariantData.attempts = attempts + 1;
          setTimeout(waitForVariantData, 200);
        }
      }
    }

    waitForVariantData();
  }

  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 100);
  }
})();
